/**
 * A track is a thing worth measuring.
 *
 * Nothing here knows what is being tracked — not food, not mood, not rainfall.
 * That is the point of the product and the easiest thing to erode: a field or a
 * branch that only makes sense for one subject area is a wrong turn, and the
 * general capability should be modelled instead.
 */

import { err, invalid, ok, type Result, type ValidationError } from './result.js'
import type { Timestamp } from './timestamp.js'

export interface EnumOption {
  readonly id: string
  readonly label: string
  /**
   * Position on the scale for an ordered enum; `null` for an unordered one.
   *
   * This is what makes `none / mild / strong` chart as a line while
   * `breakfast / lunch / dinner` can only ever be counted.
   */
  readonly score: number | null
  readonly colour?: string
  /**
   * Retired from the picker, kept for history.
   *
   * An option that has been recorded cannot be deleted without orphaning every
   * measurement that chose it — the same problem archiving solves for tracks
   * (ADR-0003). Archiving removes it from the picker while `describeValue` goes
   * on resolving it, so an entry from last year still reads "Overwhelming"
   * rather than showing a bare id.
   */
  readonly archived?: boolean
}

/** Options a user can still choose. History resolves against all of them. */
export function selectableOptions(kind: EnumKind): readonly EnumOption[] {
  return kind.options.filter((option) => option.archived !== true)
}

/**
 * A word naming one point on a numeric scale (T-807).
 *
 * Sparse **anchors**, not one label per value. A 1–10 track is ten strings and
 * a 0–100 track is a hundred; asking for either is a first-run experience
 * nobody survives. Three anchors describe the same scale, and they degrade
 * gracefully when the range is edited later — a full array silently goes wrong
 * the moment someone moves the maximum.
 */
export interface ValueWord {
  /** The point on the scale this word names. */
  readonly at: number
  readonly label: string
}

export interface NumericKind {
  readonly kind: 'numeric'
  readonly min: number
  readonly max: number
  readonly step: number
  readonly decimals: number
  /**
   * Optional words for points on the scale.
   *
   * The scale is the user's own invention, so only they can say what 8 means.
   * Once they have, the app can stop pretending the number speaks for itself —
   * on screen, and to a screen reader through `aria-valuetext`, where "8" alone
   * is close to useless.
   */
  readonly words?: readonly ValueWord[]
}

export interface BooleanKind {
  readonly kind: 'boolean'
  readonly trueLabel: string
  readonly falseLabel: string
}

export interface EnumKind {
  readonly kind: 'enum'
  readonly ordered: boolean
  readonly options: readonly EnumOption[]
}

/**
 * A discriminated union, not a `type` field beside a bag of optional
 * properties. The compiler then makes reading `min` off a boolean track
 * impossible rather than merely wrong, and adding a fourth kind turns every
 * unhandled `switch` into a compile error instead of a silent fall-through.
 */
export type TrackKind = NumericKind | BooleanKind | EnumKind

export type TrackKindName = TrackKind['kind']

export interface Track {
  readonly id: string
  readonly name: string
  readonly unit?: string
  readonly description?: string
  /**
   * How this track introduces itself when something asks for a value.
   *
   * Optional, and absent is the normal case — `questionFor` derives
   * `` `${name}?` `` when it is not set, so most tracks never need one. It
   * exists for the cases where the name and the question want to be different
   * things: a track called "Sleep quality" asking "How rested do you feel?".
   *
   * Deliberately **not** copied from the name at creation time. A stored copy
   * goes stale the moment the track is renamed, and a track that spends a year
   * asking about a name it no longer has is a bug nobody looks for.
   */
  readonly question?: string
  readonly kind: TrackKind
  /** Archived tracks keep their history and disappear from logging (ADR-0003). */
  readonly archived: boolean
  readonly position: number
  readonly createdAt: Timestamp
  readonly updatedAt: Timestamp
}

/**
 * A recorded value.
 *
 * The three kinds are distinguishable by `typeof`, so this is already a
 * discriminated union without a redundant tag — and the track it belongs to is
 * always the authority on which one is expected. A string is an enum **option
 * id**, never a label: renaming an option must not rewrite history.
 */
export type TrackValue = number | boolean | string

// ─── Exhaustive handling ──────────────────────────────────────────────────────

export interface TrackKindHandlers<T> {
  readonly numeric: (kind: NumericKind) => T
  readonly boolean: (kind: BooleanKind) => T
  readonly enum: (kind: EnumKind) => T
}

/**
 * Handles every track kind, or fails to compile.
 *
 * Prefer this to a bare `switch` at call sites that must stay total when a
 * fourth kind arrives — the missing handler becomes a type error at every
 * consumer at once.
 */
export function matchTrackKind<T>(kind: TrackKind, handlers: TrackKindHandlers<T>): T {
  switch (kind.kind) {
    case 'numeric':
      return handlers.numeric(kind)
    case 'boolean':
      return handlers.boolean(kind)
    case 'enum':
      return handlers.enum(kind)
  }
}

export const isNumericKind = (kind: TrackKind): kind is NumericKind =>
  kind.kind === 'numeric'
export const isBooleanKind = (kind: TrackKind): kind is BooleanKind =>
  kind.kind === 'boolean'
export const isEnumKind = (kind: TrackKind): kind is EnumKind => kind.kind === 'enum'

/**
 * Whether this track can produce a mean at all.
 *
 * Ordered enums can, via their scores. Unordered ones cannot: an average of
 * `breakfast / lunch / dinner` is a number that looks meaningful and is not,
 * and this app generates documents people make decisions from.
 */
export function isQuantitative(kind: TrackKind): boolean {
  return matchTrackKind(kind, {
    numeric: () => true,
    boolean: () => true,
    enum: (k) => k.ordered,
  })
}

// ─── Validation ───────────────────────────────────────────────────────────────

const MAX_NAME_LENGTH = 80
const MAX_DESCRIPTION_LENGTH = 500
/* Shorter than a description on purpose: it is a headline on a phone, and one
   that wraps to four lines has stopped being a question and become a form. */
const MAX_QUESTION_LENGTH = 120
const MAX_DECIMALS = 6
const MIN_ENUM_OPTIONS = 2
const MAX_WORD_LENGTH = 40
/* Sparse is the whole design. Past a dozen the author is labelling every value,
   which is the thing anchors exist to avoid — and a control cannot show that
   many words anyway. */
const MAX_WORDS = 12

function validateNumericKind(kind: NumericKind): ValidationError[] {
  const errors: ValidationError[] = []
  const { min, max, step, decimals } = kind

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    errors.push(
      invalid(
        'track.numeric.bounds.invalid',
        'Minimum and maximum must be numbers.',
        'kind',
      ),
    )
    return errors
  }
  if (min >= max) {
    errors.push(
      invalid(
        'track.numeric.bounds.inverted',
        `Minimum (${String(min)}) must be less than maximum (${String(max)}).`,
        'kind.min',
      ),
    )
  }
  if (!Number.isFinite(step) || step <= 0) {
    errors.push(
      invalid(
        'track.numeric.step.invalid',
        'Step must be greater than zero.',
        'kind.step',
      ),
    )
  } else if (max > min && step > max - min) {
    errors.push(
      invalid(
        'track.numeric.step.tooLarge',
        `Step (${String(step)}) is larger than the range (${String(max - min)}).`,
        'kind.step',
      ),
    )
  }
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > MAX_DECIMALS) {
    errors.push(
      invalid(
        'track.numeric.decimals.invalid',
        `Decimal places must be a whole number between 0 and ${String(MAX_DECIMALS)}.`,
        'kind.decimals',
      ),
    )
  }

  errors.push(...validateWords(kind))
  return errors
}

function validateWords(kind: NumericKind): ValidationError[] {
  const words = kind.words
  if (words === undefined || words.length === 0) {
    return []
  }

  const errors: ValidationError[] = []

  if (words.length > MAX_WORDS) {
    errors.push(
      invalid(
        'track.numeric.words.tooMany',
        `A scale can have at most ${String(MAX_WORDS)} words.`,
        'kind.words',
      ),
    )
  }

  const seen = new Set<number>()
  for (const word of words) {
    const label = word.label.trim()
    if (label === '') {
      errors.push(
        invalid('track.numeric.words.empty', 'A word needs some text.', 'kind.words'),
      )
    } else if (label.length > MAX_WORD_LENGTH) {
      errors.push(
        invalid(
          'track.numeric.words.tooLong',
          `A word can be at most ${String(MAX_WORD_LENGTH)} characters.`,
          'kind.words',
        ),
      )
    }

    if (!Number.isFinite(word.at) || word.at < kind.min || word.at > kind.max) {
      errors.push(
        invalid(
          'track.numeric.words.outOfRange',
          `"${label}" sits at ${String(word.at)}, outside the range ${String(kind.min)} to ${String(kind.max)}.`,
          'kind.words',
        ),
      )
    }

    if (seen.has(word.at)) {
      errors.push(
        invalid(
          'track.numeric.words.duplicate',
          `Two words share the point ${String(word.at)}.`,
          'kind.words',
        ),
      )
    }
    seen.add(word.at)
  }

  return errors
}

function validateBooleanKind(kind: BooleanKind): ValidationError[] {
  const errors: ValidationError[] = []
  if (kind.trueLabel.trim() === '' || kind.falseLabel.trim() === '') {
    errors.push(
      invalid(
        'track.boolean.labels.empty',
        'Both states need a label.',
        'kind.trueLabel',
      ),
    )
  } else if (kind.trueLabel.trim() === kind.falseLabel.trim()) {
    errors.push(
      invalid(
        'track.boolean.labels.duplicate',
        'The two states need different labels.',
        'kind.falseLabel',
      ),
    )
  }
  return errors
}

function validateEnumKind(kind: EnumKind): ValidationError[] {
  const errors: ValidationError[] = []
  const { options, ordered } = kind

  if (options.length < MIN_ENUM_OPTIONS) {
    errors.push(
      invalid(
        'track.enum.options.tooFew',
        `An option list needs at least ${String(MIN_ENUM_OPTIONS)} options.`,
        'kind.options',
      ),
    )
  } else if (selectableOptions(kind).length < MIN_ENUM_OPTIONS) {
    // Counted separately from the total, because archiving enough options
    // leaves a track that still validates on paper and cannot be logged
    // against — a failure that appears at the moment of use, not of editing.
    errors.push(
      invalid(
        'track.enum.options.tooFewSelectable',
        `At least ${String(MIN_ENUM_OPTIONS)} options must stay available to choose.`,
        'kind.options',
      ),
    )
  }

  const ids = new Set<string>()
  const labels = new Set<string>()
  for (const option of options) {
    if (option.label.trim() === '') {
      errors.push(
        invalid(
          'track.enum.option.label.empty',
          'Every option needs a label.',
          'kind.options',
        ),
      )
    }
    if (ids.has(option.id)) {
      errors.push(
        invalid(
          'track.enum.option.id.duplicate',
          `Duplicate option id "${option.id}".`,
          'kind.options',
        ),
      )
    }
    ids.add(option.id)

    const label = option.label.trim().toLowerCase()
    if (labels.has(label)) {
      errors.push(
        invalid(
          'track.enum.option.label.duplicate',
          `Two options are both called "${option.label.trim()}".`,
          'kind.options',
        ),
      )
    }
    labels.add(label)

    // The ordered/unordered split is the whole reason an unordered enum cannot
    // produce a mean. Half-scored options would quietly break that guarantee.
    if (ordered && option.score === null) {
      errors.push(
        invalid(
          'track.enum.option.score.missing',
          `"${option.label}" needs a position, because this scale is ordered.`,
          'kind.options',
        ),
      )
    }
    if (!ordered && option.score !== null) {
      errors.push(
        invalid(
          'track.enum.option.score.unexpected',
          `"${option.label}" has a position, but this list is unordered.`,
          'kind.options',
        ),
      )
    }
    if (option.score !== null && !Number.isFinite(option.score)) {
      errors.push(
        invalid(
          'track.enum.option.score.invalid',
          'An option position must be a number.',
          'kind.options',
        ),
      )
    }
  }

  return errors
}

export function validateTrackKind(kind: TrackKind): Result<TrackKind> {
  const errors = matchTrackKind<ValidationError[]>(kind, {
    numeric: validateNumericKind,
    boolean: validateBooleanKind,
    enum: validateEnumKind,
  })
  return errors.length > 0 ? err<TrackKind>(...errors) : ok(kind)
}

export interface ValidateTrackOptions {
  /** Used for the name-uniqueness check. Archived tracks are ignored. */
  readonly existingTracks?: readonly Track[]
}

export function validateTrack(
  track: Track,
  options: ValidateTrackOptions = {},
): Result<Track> {
  const errors: ValidationError[] = []
  const name = track.name.trim()

  if (name === '') {
    errors.push(invalid('track.name.empty', 'A track needs a name.', 'name'))
  } else if (name.length > MAX_NAME_LENGTH) {
    errors.push(
      invalid(
        'track.name.tooLong',
        `A name can be at most ${String(MAX_NAME_LENGTH)} characters.`,
        'name',
      ),
    )
  }

  const clash = (options.existingTracks ?? []).some(
    (other) =>
      other.id !== track.id &&
      !other.archived &&
      other.name.trim().toLowerCase() === name.toLowerCase(),
  )
  if (name !== '' && clash) {
    errors.push(
      invalid(
        'track.name.duplicate',
        `You already have a track called "${name}".`,
        'name',
      ),
    )
  }

  if (
    track.description !== undefined &&
    track.description.length > MAX_DESCRIPTION_LENGTH
  ) {
    errors.push(
      invalid(
        'track.description.tooLong',
        `A description can be at most ${String(MAX_DESCRIPTION_LENGTH)} characters.`,
        'description',
      ),
    )
  }

  if (track.question !== undefined && track.question.length > MAX_QUESTION_LENGTH) {
    errors.push(
      invalid(
        'track.question.tooLong',
        `A question can be at most ${String(MAX_QUESTION_LENGTH)} characters.`,
        'question',
      ),
    )
  }

  const kindResult = validateTrackKind(track.kind)
  if (!kindResult.ok) {
    errors.push(...kindResult.errors)
  }

  return errors.length > 0 ? err<Track>(...errors) : ok(track)
}

/**
 * Checks a value against the track it was recorded for.
 *
 * Errors name the specific bound or option that was broken, because the UI
 * shows them verbatim and "invalid value" tells the user nothing.
 */
export function validateTrackValue(track: Track, value: TrackValue): Result<TrackValue> {
  return matchTrackKind<Result<TrackValue>>(track.kind, {
    numeric: (kind) => {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return err<TrackValue>(
          invalid('value.type.mismatch', `${track.name} records a number.`, 'value'),
        )
      }
      if (value < kind.min) {
        return err<TrackValue>(
          invalid(
            'value.numeric.belowMinimum',
            `${track.name} cannot be below ${String(kind.min)}.`,
            'value',
          ),
        )
      }
      if (value > kind.max) {
        return err<TrackValue>(
          invalid(
            'value.numeric.aboveMaximum',
            `${track.name} cannot be above ${String(kind.max)}.`,
            'value',
          ),
        )
      }
      return ok(value)
    },
    boolean: () =>
      typeof value === 'boolean'
        ? ok(value)
        : err<TrackValue>(
            invalid('value.type.mismatch', `${track.name} records yes or no.`, 'value'),
          ),
    enum: (kind) => {
      if (typeof value !== 'string') {
        return err<TrackValue>(
          invalid(
            'value.type.mismatch',
            `${track.name} records one of its options.`,
            'value',
          ),
        )
      }
      return kind.options.some((option) => option.id === value)
        ? ok(value)
        : err<TrackValue>(
            invalid(
              'value.enum.unknownOption',
              `That is not an option on ${track.name}.`,
              'value',
            ),
          )
    },
  })
}

/**
 * The numeric position of a value, for charting and statistics.
 *
 * `null` where no meaningful number exists — an unordered enum. Callers must
 * handle that rather than defaulting to zero: "no comparable value" and "zero"
 * are different claims, and conflating them corrupts every aggregate built on
 * top (T-401).
 */
export function quantify(track: Track, value: TrackValue): number | null {
  return matchTrackKind<number | null>(track.kind, {
    numeric: () => (typeof value === 'number' ? value : null),
    boolean: () => (typeof value === 'boolean' ? (value ? 1 : 0) : null),
    enum: (kind) => {
      if (!kind.ordered || typeof value !== 'string') {
        return null
      }
      return kind.options.find((option) => option.id === value)?.score ?? null
    },
  })
}

/**
 * What this track asks (T-810).
 *
 * Derived rather than stored when the track has no question of its own, and
 * that ordering is the point: `` `${name}?` `` computed on read follows a
 * rename, where a copy taken at creation would leave a track called "Sleep"
 * asking about "Kip" forever.
 *
 * In core rather than in the screen that shows it, because the native app will
 * ask the same questions and the report may print them. A second derivation is
 * a second answer waiting to disagree with the first.
 */
export function questionFor(track: Track): string {
  const own = track.question?.trim()
  return own === undefined || own === '' ? `${track.name}?` : own
}

/**
 * The word naming this point on a numeric scale, if the author gave it one.
 *
 * **The nearest anchor at or below**, not the nearest anchor. Words on a scale
 * describe a band rather than a point — "rough" anchored at 3 means three and
 * upwards until the next word starts, so 4 is still rough. Rounding to whichever
 * anchor is closest would make 4 read as whatever 5 is called, which is a word
 * for a value the user did not give.
 *
 * A value below the lowest anchor gets no word rather than the lowest one: the
 * author chose where to start naming, and inventing a name below that is a claim
 * they did not make.
 */
export function wordFor(kind: NumericKind, value: number): string | null {
  const words = kind.words
  if (words === undefined || words.length === 0) {
    return null
  }
  let best: ValueWord | null = null
  for (const word of words) {
    if (word.at <= value && (best === null || word.at > best.at)) {
      best = word
    }
  }
  return best === null ? null : best.label.trim()
}

/** The label to show for a value, resolved through the track's configuration. */
export function describeValue(track: Track, value: TrackValue): string | null {
  return matchTrackKind<string | null>(track.kind, {
    numeric: (kind) => (typeof value === 'number' ? value.toFixed(kind.decimals) : null),
    boolean: (kind) =>
      typeof value === 'boolean' ? (value ? kind.trueLabel : kind.falseLabel) : null,
    enum: (kind) =>
      typeof value === 'string'
        ? (kind.options.find((option) => option.id === value)?.label ?? null)
        : null,
  })
}
